import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import PageTransition from '../components/PageTransition';
import { FiPlus, FiCalendar, FiClock, FiMapPin, FiUsers, FiCheck, FiX, FiLoader, FiAlertTriangle, FiEdit3, FiTrash2, FiFlag, FiPhone, FiGlobe, FiStar, FiChevronRight, FiExternalLink, FiHeart, FiEye } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RoundResponseModal from '../components/RoundResponseModal';
import EditRoundModal from '../components/EditRoundModal';
import RoundsToggle from '../components/RoundsToggle';
import OpenRoundCard from '../components/OpenRoundCard';
import AdSlot from '../components/AdSlot';
import RSVPTooltip from '../components/RSVPTooltip';
import WeatherBadge from '../components/WeatherBadge';
import BookingStatusBadge from '../components/BookingStatusBadge';
import { getRoundsWithInvitationStatus, deleteRound, getOpenRoundsForUser, undismissOpenRound } from '../services/roundService';
import { useToast } from '../contexts/ToastContext';
import { formatRoundDate, formatRoundTimeWithViewerNote, roundStartInstant } from '../utils/timeHelpers';
import { getMultipleCourseDetails } from '../services/courseDetailsService';
import { useAuth } from '../contexts/AuthContext';
import { useRoundPermissions } from '../hooks/useRoundPermissions';
import RoundsSkeleton from '../components/skeletons/RoundsSkeleton';

// Calculate dynamic round status based on invitation responses
const calculateRoundStatus = (round) => {
  if (!round.round_invitations) return 'pending';
  
  const accepted = round.round_invitations.filter(inv => inv.status === 'accepted').length;
  const declined = round.round_invitations.filter(inv => inv.status === 'declined').length;
  const pending = round.round_invitations.filter(inv => inv.status === 'pending').length;
  
  // If everyone has responded
  if (pending === 0) {
    if (accepted >= 2) return 'confirmed';
    if (accepted === 0) return 'declined';
    return 'insufficient';
  }
  
  // Still waiting for responses - don't show "pending" badge, let the response counts handle this
  return 'awaiting_responses';
};

function Rounds({ groups }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  // Segmented toggle: 'upcoming' | 'open' | 'past'.
  const [activeTab, setActiveTab] = useState('upcoming');
  const [rounds, setRounds] = useState([]);
  const [openRounds, setOpenRounds] = useState([]);
  const [openRoundsLoading, setOpenRoundsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  
  // Round editing state
  const [showEditRoundModal, setShowEditRoundModal] = useState(false);
  const [deletingRoundId, setDeletingRoundId] = useState(null);

  // Course details for quick-action icons
  const [courseDetailsMap, setCourseDetailsMap] = useState(new Map());

  // Mobile course details modal state
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [selectedCourseRoundDate, setSelectedCourseRoundDate] = useState(null);

  // Get opening hours for a specific day
  const getRoundDayHours = (courseDetails, roundDate) => {
    if (!courseDetails?.regular_opening_hours?.weekdayDescriptions) return null;
    const date = new Date(roundDate);
    const dayIndex = date.getDay(); // 0 = Sunday
    const apiIndex = dayIndex === 0 ? 6 : dayIndex - 1; // Convert to API format (Monday = 0)
    const dayStr = courseDetails.regular_opening_hours.weekdayDescriptions[apiIndex];
    if (!dayStr) return null;
    const colonIndex = dayStr.indexOf(': ');
    return colonIndex > -1 ? dayStr.substring(colonIndex + 2) : dayStr;
  };

  // Handle mobile course click - navigate to page if slug exists, otherwise show modal
  const handleCourseClick = (courseName, roundDate) => {
    const details = courseDetailsMap.get(courseName);
    if (details) {
      // If course has a slug, navigate to the dedicated page
      if (details.slug) {
        navigate(`/${details.slug}`);
        return;
      }
      // Otherwise, show the modal (fallback for courses without slugs)
      setSelectedCourseDetails(details);
      setSelectedCourseName(courseName);
      setSelectedCourseRoundDate(roundDate);
      setShowCourseModal(true);
    }
  };

  // Close mobile course modal
  const closeCourseModal = () => {
    setShowCourseModal(false);
    setSelectedCourseDetails(null);
    setSelectedCourseName('');
    setSelectedCourseRoundDate(null);
  };

  // Load rounds with invitation status
  useEffect(() => {
    loadRoundsWithInvitationStatus();
    loadOpenRounds();
  }, [groups]);

  const loadOpenRounds = async () => {
    setOpenRoundsLoading(true);
    const { data } = await getOpenRoundsForUser({ limit: 50 });
    setOpenRounds(data || []);
    setOpenRoundsLoading(false);
  };

  const handleOpenRoundJoined = (round) => {
    setOpenRounds((prev) => prev.filter((r) => r.id !== round.id));
    loadRoundsWithInvitationStatus();
  };

  // Dismissal is optimistic: the card goes immediately and the undo action
  // puts it back. The row is already written by the time the toast appears, so
  // undo deletes it rather than cancelling a pending write.
  const handleOpenRoundDismissed = (round) => {
    setOpenRounds((prev) => prev.filter((r) => r.id !== round.id));
    toast.success('Hidden. You will not see this round again.', {
      action: {
        label: 'Undo',
        onClick: async () => {
          const { error: undoError } = await undismissOpenRound(round.id);
          if (undoError) {
            toast.error('Could not restore that round.');
            return;
          }
          await loadOpenRounds();
        },
      },
    });
  };

  const loadRoundsWithInvitationStatus = async () => {
    setLoading(true);
    try {
      const { data, error } = await getRoundsWithInvitationStatus(null, user);
      
      if (error) {
        console.error('❌ Error loading rounds:', error);
        setError('Failed to load rounds');
        setRounds([]);
      } else {
        
        // Enhance rounds with group member data for better tooltips
        const enhancedRounds = data?.map(round => {
          const group = groups.find(g => g.id === round.group_id);
          return {
            ...round,
            groupMembers: group?.members || [],
            groupName: group?.name || null,
            groupInviteCode: group?.invite_code || round.group_invite_code || null
          };
        }) || [];
        
        setRounds(enhancedRounds);
        setError(null);
      }
    } catch (err) {
      console.error('💥 Exception loading rounds:', err);
      setError('Failed to load rounds');
      setRounds([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch course details for all rounds (for quick-action icons)
  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (rounds.length === 0) return;

      // Extract unique course names
      const courseNames = rounds
        .map(round => round.course)
        .filter(Boolean);

      if (courseNames.length === 0) return;

      try {
        const detailsMap = await getMultipleCourseDetails(courseNames);
        setCourseDetailsMap(detailsMap);
      } catch (error) {
        console.error('Error fetching course details:', error);
      }
    };

    fetchCourseDetails();
  }, [rounds]);

  const handleRespondToRound = async (round) => {
    setSelectedRound(round);
    setShowResponseModal(true);
  };

  const handleResponseSuccess = async () => {
    // Refresh all rounds data to show updated counts and statuses
    await loadRoundsWithInvitationStatus();
  };

  // Round action handlers
  const handleEditRound = (round) => {
    setSelectedRound(round);
    setShowEditRoundModal(true);
  };

  const handleRoundUpdateSuccess = async () => {
    await loadRoundsWithInvitationStatus();
  };

  const handleRoundDeleteSuccess = async () => {
    await loadRoundsWithInvitationStatus();
  };

  const handleDirectDelete = async (round) => {
    if (window.confirm('Are you sure you want to delete this round? This action cannot be undone.')) {
      setDeletingRoundId(round.id);
      try {
        const { error: deleteError } = await deleteRound(round.id);
        if (deleteError) {
          console.error('Error deleting round:', deleteError);
          alert('Failed to delete round. Please try again.');
        } else {
          await loadRoundsWithInvitationStatus();
        }
      } catch (err) {
        console.error('Exception deleting round:', err);
        alert('Failed to delete round. Please try again.');
      } finally {
        setDeletingRoundId(null);
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return { className: 'bg-status-success/10 text-status-success border-status-success/30', icon: FiCheck, text: 'Confirmed' };
      case 'declined':
        return { className: 'bg-status-error/10 text-status-error border-status-error/30', icon: FiX, text: 'Declined' };
      case 'insufficient':
        return { className: 'bg-status-warning/10 text-status-warning border-status-warning/30', icon: FiAlertTriangle, text: 'Insufficient Players' };
      case 'cancelled':
        return { className: 'bg-status-error/10 text-status-error border-status-error/30', icon: FiX, text: 'Cancelled' };
      // REMOVED: 'pending' case - we don't want to show "Pending" badge
      // REMOVED: 'awaiting_responses' case - we don't want to show any badge for this state
      default:
        return null; // Return null instead of showing "Unknown"
    }
  };

  const getInvitationStatusBadge = (invitationStatus) => {
    if (!invitationStatus) return null;

    switch (invitationStatus) {
      case 'accepted':
        return { className: 'bg-status-success/10 text-status-success border-status-success/30', icon: FiCheck, text: 'Accepted' };
      case 'declined':
        return { className: 'bg-status-error/10 text-status-error border-status-error/30', icon: FiX, text: 'Declined' };
      // REMOVED: No longer showing pending invitation status badge
      default:
        return null;
    }
  };

  // Filter/sort by the round's actual start instant (date + time + tz),
  // not just round.date. Without this, a UK 06:30 BST round shown to an
  // LA viewer would be mis-bucketed at the day boundary because
  // `new Date('2026-05-31')` parses as UTC midnight and shifts back to
  // the previous calendar day west of UTC.
  const nowMs = Date.now();
  const filteredRounds = rounds.filter(round => {
    const instant = roundStartInstant(round);
    if (!instant) return false;
    if (activeTab === 'upcoming') return instant.getTime() >= nowMs;
    if (activeTab === 'past') return instant.getTime() < nowMs;
    return false;
  }).sort((a, b) => {
    const ai = roundStartInstant(a)?.getTime() ?? 0;
    const bi = roundStartInstant(b)?.getTime() ?? 0;
    return activeTab === 'upcoming' ? ai - bi : bi - ai;
  });

  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    } catch {
      return timeString; // Return original if parsing fails
    }
  };

  // Format player names grouped by RSVP status
  const PlayerNames = ({ invitations, groupId, roundType }) => {
    const [expanded, setExpanded] = useState(false);

    if (!invitations || invitations.length === 0) return null;
    if (!groupId && roundType !== 'friends') return null;
    if (!invitations.some(inv => inv.member_name)) return null;

    const accepted = invitations.filter(inv => inv.status === 'accepted');
    const pending = invitations.filter(inv => inv.status === 'pending');
    const declined = invitations.filter(inv => inv.status === 'declined');

    if (accepted.length === 0 && pending.length === 0 && declined.length > 0) {
      return <p className="ml-8 text-sm text-status-error">All declined</p>;
    }

    // Detect duplicate first names to decide when to show last initial
    const firstNameCounts = {};
    invitations.forEach(inv => {
      const first = (inv.member_name || 'Unknown').split(' ')[0];
      firstNameCounts[first] = (firstNameCounts[first] || 0) + 1;
    });

    const getDisplayName = (inv) => {
      const name = inv.member_name || 'Unknown';
      const parts = name.split(' ');
      const first = parts[0];
      if (parts.length > 1 && firstNameCounts[first] > 1) {
        return `${first} ${parts[1][0]}.`;
      }
      return first;
    };

    const maxShow = 3;
    const isTruncated = accepted.length > maxShow || pending.length > maxShow;

    const joinNames = (list) => {
      const names = list.map(getDisplayName);
      const limit = expanded ? names.length : maxShow;
      if (!expanded && names.length > maxShow) {
        return `${names.slice(0, limit).join(', ')} +${names.length - limit} more`;
      }
      if (names.length === 2) return names.join(' & ');
      if (names.length === 1) return names[0];
      return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
    };

    const segments = [];
    if (accepted.length > 0) segments.push(<span key="a" className="text-status-success">{joinNames(accepted)} confirmed</span>);
    if (pending.length > 0) segments.push(<span key="p" className="text-status-warning">{joinNames(pending)} pending</span>);

    if (segments.length === 0) return null;

    const content = segments.reduce((acc, seg, i) => i === 0 ? [seg] : [...acc, <span key={`dot-${i}`}> &bull; </span>, seg], []);

    return (
      <p
        className={`ml-8 text-sm text-text-muted ${isTruncated ? 'cursor-pointer' : ''}`}
        onClick={isTruncated ? () => setExpanded(e => !e) : undefined}
        title={isTruncated && !expanded ? 'Tap to show all' : undefined}
      >
        {content}
      </p>
    );
  };

  // Round Card Component
  const RoundCard = ({ round, index }) => {
    const dynamicStatus = calculateRoundStatus(round);
    const statusBadge = getStatusBadge(dynamicStatus);
    const invitationBadge = getInvitationStatusBadge(round.user_invitation_status);
    const permissions = useRoundPermissions(round, groups);

    const courseDetails = round.course ? courseDetailsMap.get(round.course) : null;
    const roundDayHours = courseDetails ? getRoundDayHours(courseDetails, round.date) : null;

    return (
      <div
        key={round.id}
        className="bg-card rounded-lg border border-border-subtle p-6 transition-all duration-300"
      >
        {/* Hero Date/Time Section */}
        <div className="pb-5 mb-5 border-b border-border-subtle">
          {/* Row 1: Date/time + action buttons */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-h4 text-text-primary">
                <span className="hidden sm:inline">
                  {formatRoundDate(round, { weekday: 'long', day: 'numeric', month: 'long', year: undefined })}
                </span>
                <span className="sm:hidden">
                  {formatRoundDate(round, { weekday: 'short', day: 'numeric', month: 'short', year: undefined })}
                </span>
              </p>
              {(() => {
                if (!round.time) return <p className="text-base font-bold text-accent mt-0.5">Time TBD</p>;
                const { primary, secondary } = formatRoundTimeWithViewerNote(round, { hour12: true });
                return (
                  <>
                    <p className="text-base font-bold text-accent mt-0.5">{primary}</p>
                    {secondary && (
                      <p className="text-xs text-text-secondary mt-0.5">{secondary}</p>
                    )}
                  </>
                );
              })()}
            </div>
            {/* Action buttons - top right */}
            <div className="flex-shrink-0 flex items-center gap-2">
              {round.user_invitation_status === 'pending' && activeTab === 'upcoming' && (
                <button
                  onClick={() => handleRespondToRound(round)}
                  className="inline-flex items-center px-4 py-1.5 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 text-xs font-bold"
                >
                  Respond
                </button>
              )}
              {round.short_code && (
                <Link
                  to={`/round/${round.short_code}`}
                  className="inline-flex items-center px-4 py-1.5 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 text-xs font-bold"
                >
                  <SafeIcon icon={FiEye} className="w-3.5 h-3.5 mr-1.5" />
                  View
                </Link>
              )}
              {activeTab === 'past' && permissions.canDelete && (
                <button
                  onClick={() => handleDirectDelete(round)}
                  disabled={deletingRoundId === round.id}
                  className="inline-flex items-center px-4 py-1.5 bg-status-error text-white rounded-lg hover:brightness-90 transition-all duration-200 text-xs font-bold disabled:opacity-50"
                >
                  {deletingRoundId === round.id ? (
                    <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5 mr-1.5" />Delete</>
                  )}
                </button>
              )}
            </div>
          </div>
          {/* Row 2: Badges (score, booking status, invitation status) */}
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {round.weather_golf_score && (
              <WeatherBadge
                golfScore={round.weather_golf_score}
                golfCategory={round.weather_golf_category}
                compact
              />
            )}
            {round.course && round.course !== 'To be determined' && (
              <BookingStatusBadge isBooked={round.is_booked || false} />
            )}
            {/* RSVP status - only for rounds user didn't create. On an upcoming
                round an answered badge is tappable, because this is where users
                look for their answer and it used to be a dead end. Past rounds
                keep the plain span: responses close at the start instant. */}
            {round.created_by !== user?.id && round.user_invitation_status && (() => {
              const statusConfig = {
                accepted: { icon: FiCheck, text: 'Accepted', className: 'text-status-success' },
                declined: { icon: FiX, text: 'Declined', className: 'text-status-error' },
                pending: { icon: FiAlertTriangle, text: 'Not responded', className: 'text-status-warning' },
              };
              const config = statusConfig[round.user_invitation_status];
              if (!config) return null;

              const canChange =
                activeTab === 'upcoming' &&
                (round.user_invitation_status === 'accepted' ||
                  round.user_invitation_status === 'declined');

              if (!canChange) {
                return (
                  <span className={`inline-flex items-center text-xs font-medium ${config.className}`}>
                    <SafeIcon icon={config.icon} className="w-3.5 h-3.5 mr-1" />
                    {config.text}
                  </span>
                );
              }

              return (
                <button
                  type="button"
                  onClick={() => handleRespondToRound(round)}
                  className={`inline-flex items-center text-xs font-medium rounded-md px-1.5 py-0.5 -mx-1.5 hover:bg-surface transition-colors ${config.className}`}
                >
                  <SafeIcon icon={config.icon} className="w-3.5 h-3.5 mr-1" />
                  {config.text}
                  <span className="text-text-muted ml-1.5 font-normal">· Change</span>
                </button>
              );
            })()}
            {activeTab === 'past' && statusBadge && (
              <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium ${statusBadge.className}`}>
                <SafeIcon icon={statusBadge.icon} className="w-3.5 h-3.5 mr-1.5" />
                {statusBadge.text}
              </div>
            )}
          </div>
        </div>

        {/* Desktop: 50/50 split - details left, weather right */}
        <div className="md:flex md:gap-8">
          {/* Left side: Course, Holes, Players */}
          <div className="md:w-1/2">
            {/* Course Details Section */}
            {round.course && (
              <div className="text-text-secondary mb-3">
                {/* Mobile: Course name only */}
                <div className="md:hidden flex items-center">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-text-muted mr-3 flex-shrink-0" />
                  <span className="font-medium">{round.course}</span>
                </div>

                {/* Desktop: Course name with inline details */}
                <div className="hidden md:block">
                  <div className="flex items-start">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-text-muted mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium">{round.course}</p>
                      {courseDetails && (
                        <div className="mt-2 space-y-1 text-sm">
                          {courseDetails.phone && (
                            <a
                              href={`tel:${courseDetails.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center text-text-muted hover:text-accent transition-colors"
                            >
                              <SafeIcon icon={FiPhone} className="w-3.5 h-3.5 mr-2 text-text-muted" />
                              {courseDetails.phone}
                            </a>
                          )}
                          {courseDetails.website && (
                            <a
                              href={courseDetails.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center text-text-muted hover:text-accent transition-colors"
                            >
                              <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5 mr-2 text-text-muted" />
                              <span>Website</span>
                              <SafeIcon icon={FiExternalLink} className="w-3 h-3 ml-1 text-text-muted" />
                            </a>
                          )}
                          {roundDayHours && (
                            <div className="flex items-center text-text-muted">
                              <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-2 text-text-muted" />
                              <span>{roundDayHours}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Holes and Players */}
            <div className="space-y-3 mb-5">
              <div className="flex items-center text-text-secondary">
                <SafeIcon icon={FiFlag} className="w-5 h-5 text-text-muted mr-3" />
                <p className="font-medium">{round.hole_count || 18} Holes</p>
              </div>

              {round.round_invitations && (
                <div className="space-y-1">
                  <div className="flex items-center text-text-secondary">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 text-text-muted mr-3" />
                    <p className="font-medium">
                      {round.round_invitations.length} {round.round_invitations.length === 1 ? 'player' : 'players'}
                      {round.groupName && (
                        <span className="font-normal"> · <Link to={`/group/${round.groupInviteCode || round.group_id}`} className="text-blue-600 font-bold hover:text-blue-800 transition-colors">{round.groupName}</Link></span>
                      )}
                    </p>
                  </div>
                  <PlayerNames invitations={round.round_invitations} groupId={round.group_id} roundType={round.round_type} />
                </div>
              )}
            </div>
          </div>

          {/* Right side: Weather (desktop only). UV + alerts intentionally
              omitted to keep the metric grid aligned with /round/<shortCode>
              (the RPC powering that page doesn't return uv_index either). */}
          {activeTab === 'upcoming' && round.weather_golf_score && (
            <div className="hidden md:block md:w-1/2 md:border-l md:border-border-subtle md:pl-8">
              <WeatherBadge
                golfScore={round.weather_golf_score}
                golfCategory={round.weather_golf_category}
                temperature={round.weather_temperature}
                windSpeed={round.weather_wind_speed}
                precipitation={round.weather_precipitation}
                precipitationLabel={round.weather_precipitation_label}
                humidity={round.weather_humidity}
                isUnplayable={round.weather_is_unplayable}
                groundCategory={round.weather_ground_category}
                groundRainfall={round.weather_ground_rainfall}
                groundHistoryDays={round.weather_ground_history_days}
                daylightCategory={round.weather_daylight_category}
                daylightScore={round.weather_daylight_score}
                sunrise={round.weather_sunrise}
                sunset={round.weather_sunset}
              />
            </div>
          )}
        </div>

        {/* Mobile-only: Weather removed - score shown in header, full weather on round detail page */}
        {false && (
          <div className="md:hidden mb-5 pt-3 border-t border-border-subtle">
            <WeatherBadge
              golfScore={round.weather_golf_score}
              golfCategory={round.weather_golf_category}
              temperature={round.weather_temperature}
              windSpeed={round.weather_wind_speed}
              precipitation={round.weather_precipitation}
              precipitationLabel={round.weather_precipitation_label}
              humidity={round.weather_humidity}
              uvIndex={round.weather_uv_index}
              alerts={round.weather_alerts}
              isUnplayable={round.weather_is_unplayable}
              groundCategory={round.weather_ground_category}
              groundRainfall={round.weather_ground_rainfall}
              groundHistoryDays={round.weather_ground_history_days}
              daylightCategory={round.weather_daylight_category}
              daylightScore={round.weather_daylight_score}
              sunrise={round.weather_sunrise}
              sunset={round.weather_sunset}
            />
          </div>
        )}

      </div>
    );
  };

  if (loading) {
    return (
      <>
        <SEO title="My Rounds | TeeSync" noindex={true} />
        <RoundsSkeleton />
      </>
    );
  }

  return (
    <>
      <SEO title="My Rounds | TeeSync" noindex={true} />
      <div className="min-h-screen bg-app">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PageTransition className="space-y-6 md:space-y-12">
            {/* Page Header - on mobile the New Round button stacks above the
                tabs (full-width, easy thumb target); from sm: up they sit on
                the same row with the button right-aligned. */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <RoundsToggle
                rounds={rounds}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
              <Link
                to="/create-round"
                className="w-full sm:w-auto min-h-[44px] inline-flex items-center justify-center px-5 py-2.5 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium text-sm whitespace-nowrap"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1.5" />
                New Round
              </Link>
            </div>

            {/* Error State */}
            {error && (
              <div>
                <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-6 text-center">
                  <p className="text-status-error font-medium">{error}</p>
                  <button
                    onClick={loadRoundsWithInvitationStatus}
                    className="mt-4 px-4 py-2 bg-status-error text-white rounded-md hover:brightness-90 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Rounds List */}
            <div className="space-y-6">
              {/* Joinable open-invite rounds — only on Upcoming, grouped at the
                  top as compact preview cards, visually distinct from the
                  user's own rounds below. */}
              {activeTab === 'upcoming' && openRounds.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                      Rounds you can join
                    </h3>
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-accent/10 text-accent text-xs font-bold rounded-full">
                      {openRounds.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {openRounds.map((r) => (
                      <OpenRoundCard
                        key={r.id}
                        round={r}
                        onJoined={handleOpenRoundJoined}
                        onDismiss={handleOpenRoundDismissed}
                      />
                    ))}
                  </div>
                </div>
              )}

              {filteredRounds.length > 0 ? (
                <div className="grid grid-cols-1 gap-6">
                  {filteredRounds.flatMap((round, index) => {
                    const items = [
                      <RoundCard key={round.id} round={round} index={index} />,
                    ];
                    // Native-style in-feed ad after every 3rd card (free web
                    // mobile only; AdSlot renders null otherwise so no gap is
                    // added on desktop, for Pro, or on iOS).
                    if ((index + 1) % 3 === 0 && index < filteredRounds.length - 1) {
                      items.push(
                        <AdSlot
                          key={`infeed-ad-${round.id}`}
                          variant="infeed"
                          className="bg-card rounded-lg border border-border-subtle p-6"
                        />,
                      );
                    }
                    return items;
                  })}
                </div>
              ) : (
                /* Empty state only when there's nothing in either list. */
                !(activeTab === 'upcoming' && openRounds.length > 0) && (
                  <div
                    className="text-center py-12 xs:py-16 px-4 xs:px-6 sm:px-8 bg-card rounded-lg border border-border-subtle"
                  >
                    <div className="w-16 h-16 bg-surface rounded-lg flex items-center justify-center mx-auto mb-4 border border-border-subtle">
                      <SafeIcon icon={FiCalendar} className="w-8 h-8 text-text-muted" />
                    </div>
                    <h3 className="text-h3 text-text-primary">
                      {activeTab === 'upcoming' ? 'No rounds scheduled' : 'No past rounds'}
                    </h3>
                    {activeTab !== 'upcoming' && (
                      <p className="text-body text-text-secondary mt-3">
                        No completed rounds to display yet
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          </PageTransition>
        </div>
      </div>

      {/* Round Response Modal */}
      <RoundResponseModal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        round={selectedRound}
        currentStatus={selectedRound?.user_invitation_status ?? null}
        onResponseSuccess={handleResponseSuccess}
      />

      {/* Edit Round Modal */}
      <EditRoundModal
        isOpen={showEditRoundModal}
        onClose={() => setShowEditRoundModal(false)}
        round={selectedRound}
        onUpdateSuccess={handleRoundUpdateSuccess}
        onDeleteSuccess={handleRoundDeleteSuccess}
      />

      {/* Mobile Course Details Modal */}
      <AnimatePresence>
        {showCourseModal && selectedCourseDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bottom-20 z-50 md:hidden"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={closeCourseModal}
            />
            {/* Modal content - bottom sheet style */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-card rounded-t-xl p-6 max-h-[70vh] overflow-y-auto "
            >
              {/* Header with course name */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-h4 text-text-primary">{selectedCourseName}</h3>
                <button
                  onClick={closeCourseModal}
                  className="w-8 h-8 bg-surface rounded-full flex items-center justify-center hover:bg-card transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
                </button>
              </div>

              {/* Course details */}
              <div className="space-y-4">
                {/* Phone */}
                {selectedCourseDetails.phone && (
                  <a
                    href={`tel:${selectedCourseDetails.phone}`}
                    className="flex items-center p-3 bg-app rounded-xl hover:bg-surface transition-colors"
                  >
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiPhone} className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">Phone</p>
                      <p className="font-medium text-text-primary">{selectedCourseDetails.phone}</p>
                    </div>
                  </a>
                )}

                {/* Website */}
                {selectedCourseDetails.website && (
                  <a
                    href={selectedCourseDetails.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 bg-app rounded-xl hover:bg-surface transition-colors"
                  >
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiGlobe} className="w-5 h-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-text-muted">Website</p>
                      <p className="font-medium text-text-primary">Visit website</p>
                    </div>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-text-muted" />
                  </a>
                )}

                {/* Address */}
                {selectedCourseDetails.address && (
                  <div className="flex items-start p-3 bg-app rounded-xl">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <SafeIcon icon={FiMapPin} className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">Address</p>
                      <p className="font-medium text-text-primary">{selectedCourseDetails.address}</p>
                    </div>
                  </div>
                )}

                {/* Rating */}
                {selectedCourseDetails.rating && (
                  <div className="flex items-center p-3 bg-app rounded-xl">
                    <div className="w-10 h-10 bg-status-warning/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiStar} className="w-5 h-5 text-status-warning" />
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">Rating</p>
                      <p className="font-medium text-text-primary">
                        {selectedCourseDetails.rating.toFixed(1)}
                        {selectedCourseDetails.user_rating_count && (
                          <span className="text-text-muted font-normal ml-1">
                            ({selectedCourseDetails.user_rating_count.toLocaleString()} reviews)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Opening hours for the round date */}
                {selectedCourseRoundDate && getRoundDayHours(selectedCourseDetails, selectedCourseRoundDate) && (
                  <div className="flex items-center p-3 bg-app rounded-xl">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                      <SafeIcon icon={FiClock} className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-text-muted">
                        Open on {new Date(selectedCourseRoundDate).toLocaleDateString('en-GB', { weekday: 'long' })}
                      </p>
                      <p className="font-medium text-text-primary">{getRoundDayHours(selectedCourseDetails, selectedCourseRoundDate)}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Rounds;